'use client'

import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useActionState, useEffect, useRef, useState } from 'react'
import { Mail } from 'lucide-react'

import {
  magicLinkLogin,
  requestPasswordReset,
  signIn,
  verifyMagicLinkCode,
} from '@/app/actions/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { resolveAuthLanding, sanitizeAuthRedirect, signUpHref } from '@/constants/authRoutes'

import { AuthDivider } from './AuthDivider'
import { CodeVerificationForm } from './CodeVerificationForm'
import { OAuthSection } from './OAuthSection'

import type { AuthResult, RequestPasswordResetResult, VerifyEmailResult } from '@/app/actions/auth'

type SignInMethod = 'password' | 'magicLink'
type FormVariant = 'page' | 'modal'
type AuthenticatedHandler = () => void | Promise<void>

function SkipRedirectField({ variant }: { variant: FormVariant }) {
  if (variant !== 'modal') return null
  return <input type="hidden" name="skipRedirect" value="1" />
}

function useAuthSuccessEffect(
  shouldComplete: boolean,
  onAuthenticated: AuthenticatedHandler | undefined,
) {
  const handledRef = useRef(false)

  useEffect(() => {
    if (!shouldComplete || handledRef.current) return
    handledRef.current = true
    void onAuthenticated?.()
  }, [onAuthenticated, shouldComplete])
}

const titleClass = 'text-2xl font-semibold font-family-body text-foreground'
const descriptionClass =
  'text-sm text-muted-foreground flex items-center justify-center gap-2 font-family-body'

function getContainerClass(variant: FormVariant): string {
  return variant === 'modal'
    ? 'mx-auto w-full space-y-6 p-6'
    : 'mx-auto w-full space-y-6 rounded-2xl border border-border p-6'
}

const SignUpLink = ({
  redirectTo,
  onSwitchToSignUp,
}: {
  redirectTo: string | null
  onSwitchToSignUp?: () => void
}) => {
  const searchParams = useSearchParams()
  const resolvedRedirectTo = sanitizeAuthRedirect(redirectTo ?? searchParams.get('redirectTo'))
  const href = signUpHref(resolvedRedirectTo)

  if (onSwitchToSignUp) {
    return (
      <div className="m-auto w-fit text-center">
        <Button
          type="button"
          variant="link"
          className="text-sm text-muted-foreground transition-colors hover:text-primary"
          onClick={onSwitchToSignUp}
        >
          Don&apos;t have an account? <span className="font-medium text-primary">Sign up</span>
        </Button>
      </div>
    )
  }

  return (
    <div className="m-auto w-fit text-center">
      <Button
        type="button"
        variant="link"
        className="text-sm text-muted-foreground transition-colors hover:text-primary"
        asChild
      >
        <Link href={href}>
          Don&apos;t have an account? <span className="font-medium text-primary">Sign up</span>
        </Link>
      </Button>
    </div>
  )
}

const ForgotPasswordSignin = ({
  email,
  onEmailChange,
  onBack,
  variant = 'page',
}: {
  email: string
  onEmailChange: (email: string) => void
  onBack: () => void
  variant?: FormVariant
}) => {
  const [forgotPasswordState, forgotPasswordAction, isPending] = useActionState<
    RequestPasswordResetResult | null,
    FormData
  >(requestPasswordReset, null)

  return (
    <div className={getContainerClass(variant)}>
      <div className="space-y-2 text-center">
        <h1 className={titleClass}>Reset your password</h1>
        <p className={descriptionClass}>
          <span className="block">Enter your email and we&apos;ll send you a reset link</span>
        </p>
      </div>
      <form action={forgotPasswordAction} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="forgot-email">Email</Label>
          <Input
            id="forgot-email"
            name="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            required
            disabled={isPending}
          />
        </div>
        {forgotPasswordState?.error && (
          <p className="rounded-md bg-red-50 p-3 text-center text-sm text-red-600">
            {forgotPasswordState.error}
          </p>
        )}
        {forgotPasswordState?.success && (
          <p className="rounded-md bg-green-50 p-3 text-center text-sm text-green-700">
            If an account exists with this email, we&apos;ve sent it a password reset link.
          </p>
        )}
        <Button type="submit" variant="accent" className="w-full" disabled={isPending || !email}>
          {isPending ? 'Sending…' : 'Send reset link'}
        </Button>
        <Button
          type="button"
          variant="link"
          className="w-full justify-center text-center text-sm text-muted-foreground"
          onClick={onBack}
          disabled={isPending}
        >
          Back to sign in
        </Button>
      </form>
    </div>
  )
}

const PasswordSignin = ({
  email,
  onEmailChange,
  onSwitchToMagicLink,
  variant = 'page',
  redirectTo,
  onSwitchToSignUp,
  onAuthenticated,
}: {
  email: string
  onEmailChange: (email: string) => void
  onSwitchToMagicLink: () => void
  variant?: FormVariant
  redirectTo: string | null
  onSwitchToSignUp?: () => void
  onAuthenticated?: AuthenticatedHandler
}) => {
  const searchParams = useSearchParams()
  const resolvedRedirectTo = resolveAuthLanding(redirectTo ?? searchParams.get('redirectTo'))
  const resetSuccess = searchParams.get('reset') === 'success'

  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [signInState, signInAction, isLoading] = useActionState<AuthResult | null, FormData>(
    signIn,
    null,
  )
  const [password, setPassword] = useState('')

  useAuthSuccessEffect(
    Boolean(signInState?.success && !signInState.needsEmailVerification),
    onAuthenticated,
  )

  if (showForgotPassword) {
    return (
      <ForgotPasswordSignin
        email={email}
        onEmailChange={onEmailChange}
        onBack={() => setShowForgotPassword(false)}
        variant={variant}
      />
    )
  }

  return (
    <div className={getContainerClass(variant)}>
      <div className="space-y-2 text-center">
        <h1 className={titleClass}>Sign in to your account</h1>
      </div>
      <OAuthSection redirectTo={resolvedRedirectTo} />
      <AuthDivider text="Or" />
      <p className={descriptionClass}>
        <span className="block">Enter your email address and password to continue</span>
      </p>
      {resetSuccess && (
        <div
          className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
          role="alert"
        >
          Password reset. Sign in with your new password.
        </div>
      )}
      <form action={signInAction} className="space-y-4">
        <input type="hidden" name="redirectTo" value={resolvedRedirectTo ?? ''} />
        <SkipRedirectField variant={variant} />
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        {signInState?.error && (
          <div className="rounded-md bg-red-50 p-3 text-center text-sm text-red-600">
            {signInState.error}
          </div>
        )}

        <Button
          type="submit"
          variant="accent"
          className="w-full"
          disabled={isLoading || !email || !password}
        >
          {isLoading ? 'Loading...' : 'Sign in'}
        </Button>
        <Button
          type="button"
          variant="link"
          className="w-full justify-center text-center text-sm text-muted-foreground"
          onClick={() => setShowForgotPassword(true)}
          disabled={isLoading}
        >
          Forgot password?
        </Button>
        <Button
          type="button"
          variant="link"
          className="w-full justify-center text-center text-sm text-muted-foreground"
          onClick={onSwitchToMagicLink}
          disabled={isLoading}
        >
          Email me a code instead
        </Button>
      </form>
      <AuthDivider />
      <SignUpLink redirectTo={resolvedRedirectTo} onSwitchToSignUp={onSwitchToSignUp} />
    </div>
  )
}

const MagicLinkWaiting = ({
  email,
  onSwitchToPassword,
  variant = 'page',
  redirectTo,
  onAuthenticated,
}: {
  email: string
  onSwitchToPassword: () => void
  variant?: FormVariant
  redirectTo: string | null
  onAuthenticated?: AuthenticatedHandler
}) => {
  const searchParams = useSearchParams()
  const resolvedRedirectTo = resolveAuthLanding(redirectTo ?? searchParams.get('redirectTo'))

  const [verifyState, verifyAction, isPending] = useActionState<VerifyEmailResult | null, FormData>(
    verifyMagicLinkCode,
    null,
  )

  useAuthSuccessEffect(Boolean(verifyState?.success), onAuthenticated)
  const [isResendLoading, setIsResendLoading] = useState(false)
  const [resendState, setResendState] = useState<VerifyEmailResult | null>(null)

  const handleResendCode = async () => {
    setIsResendLoading(true)
    setResendState(null)
    try {
      const result = await magicLinkLogin(email)
      setResendState(result)
    } finally {
      setIsResendLoading(false)
    }
  }

  return (
    <div className={getContainerClass(variant)}>
      <div className="space-y-2 text-center">
        <h1 className={titleClass}>Check your email</h1>
        <p className={descriptionClass}>
          <span className="block">
            We sent a sign-in code to your email. Enter it below to continue.
          </span>
        </p>
      </div>
      <form action={verifyAction} className="space-y-4">
        <input type="hidden" name="redirectTo" value={resolvedRedirectTo ?? ''} />
        <SkipRedirectField variant={variant} />
        <CodeVerificationForm
          mode="magicLinkLogin"
          isPending={isPending}
          codeFormError={verifyState?.error}
          email={email}
          onResendCode={handleResendCode}
          isResendLoading={isResendLoading}
          resendState={resendState}
          asFormContents
        />
      </form>
      <Button
        type="button"
        variant="link"
        className="w-full justify-center text-center text-sm text-muted-foreground"
        onClick={onSwitchToPassword}
        disabled={isPending || isResendLoading}
      >
        Sign in with password
      </Button>
    </div>
  )
}

const MagicLinkSignin = ({
  email,
  onEmailChange,
  onSwitchToPassword,
  variant = 'page',
  redirectTo,
  onSwitchToSignUp,
  onAuthenticated,
}: {
  email: string
  onEmailChange: (email: string) => void
  onSwitchToPassword: () => void
  variant?: FormVariant
  redirectTo: string | null
  onSwitchToSignUp?: () => void
  onAuthenticated?: AuthenticatedHandler
}) => {
  const searchParams = useSearchParams()
  const resolvedRedirectTo = resolveAuthLanding(redirectTo ?? searchParams.get('redirectTo'))

  const [codeSent, setCodeSent] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)

  const handleSendMagicLink = async () => {
    setSendError(null)
    setIsSending(true)
    try {
      const result = await magicLinkLogin(email)
      if (result.success) {
        setCodeSent(true)
      } else {
        setSendError(result.error ?? 'Failed to send login link.')
      }
    } finally {
      setIsSending(false)
    }
  }

  if (codeSent) {
    return (
      <MagicLinkWaiting
        email={email}
        onSwitchToPassword={onSwitchToPassword}
        variant={variant}
        redirectTo={resolvedRedirectTo}
        onAuthenticated={onAuthenticated}
      />
    )
  }

  return (
    <div className={getContainerClass(variant)}>
      <div className="space-y-2 text-center">
        <h1 className={titleClass}>Sign in to your account</h1>
      </div>
      <OAuthSection redirectTo={resolvedRedirectTo} />
      <AuthDivider text="Or" />
      <p className={descriptionClass}>
        <span className="block">Enter your email address to continue</span>
      </p>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              onEmailChange(e.target.value)
              setSendError(null)
            }}
            required
            disabled={isSending}
          />
        </div>

        <Button
          type="button"
          variant="accent"
          className="w-full"
          disabled={isSending || !email}
          onClick={handleSendMagicLink}
          customIcon={<Mail className="size-4" />}
        >
          {isSending ? 'Sending…' : 'Get login code'}
        </Button>
        {sendError && (
          <p className="rounded-md bg-red-50 p-3 text-center text-sm text-red-600">{sendError}</p>
        )}
        <Button
          type="button"
          variant="link"
          className="w-full justify-center text-center text-sm text-muted-foreground"
          onClick={onSwitchToPassword}
          disabled={isSending}
        >
          Sign in with password
        </Button>
      </div>
      <AuthDivider />
      <SignUpLink redirectTo={resolvedRedirectTo} onSwitchToSignUp={onSwitchToSignUp} />
    </div>
  )
}

type SignInFormProps = {
  variant?: FormVariant
  redirectTo?: string | null
  onSwitchToSignUp?: () => void
  onAuthenticated?: AuthenticatedHandler
}

const SignInForm = ({
  variant = 'page',
  redirectTo: propRedirectTo = null,
  onSwitchToSignUp,
  onAuthenticated,
}: SignInFormProps) => {
  const searchParams = useSearchParams()
  const [signInMethod, setSignInMethod] = useState<SignInMethod>(() =>
    searchParams.get('reset') === 'success' ? 'password' : 'magicLink',
  )
  const [email, setEmail] = useState('')

  switch (signInMethod) {
    case 'password':
      return (
        <PasswordSignin
          email={email}
          onEmailChange={setEmail}
          onSwitchToMagicLink={() => setSignInMethod('magicLink')}
          variant={variant}
          redirectTo={propRedirectTo}
          onSwitchToSignUp={onSwitchToSignUp}
          onAuthenticated={onAuthenticated}
        />
      )
    case 'magicLink':
      return (
        <MagicLinkSignin
          email={email}
          onEmailChange={setEmail}
          onSwitchToPassword={() => setSignInMethod('password')}
          variant={variant}
          redirectTo={propRedirectTo}
          onSwitchToSignUp={onSwitchToSignUp}
          onAuthenticated={onAuthenticated}
        />
      )
    default:
      throw new Error(`Unhandled sign in method: ${signInMethod}`)
  }
}

export default SignInForm
